import type { ReactNode } from "react";
import { cn } from "@nexus/ui";

interface ToggleChipProps {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  /** Optional badge count, e.g. number of archived rows hidden behind the toggle. */
  count?: number;
  title?: string;
}

/**
 * Boolean filter pill (e.g. "Show archived", "No role"). Single source for
 * the toggle-chip markup previously duplicated across the Org, People, and
 * Roles toolbars.
 */
export function ToggleChip({ icon, label, active, onClick, count, title }: ToggleChipProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={title}
      className={cn(
        "flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium transition-colors",
        active
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border bg-transparent text-muted-foreground hover:border-foreground/30 hover:text-foreground",
      )}
    >
      {icon}
      <span>{label}</span>
      {/* `leading-none` is load-bearing: the arbitrary text size sets no
          line-height, so without it the badge inherits the button's 16px one,
          fills the 16px circle with a full line box and lets the font's
          baseline place the digit instead of centring it.
          The fill is an alpha of the foreground rather than `bg-muted`, which
          is #f9fafb against a #ffffff card and so does not read as a circle at
          all — the same holds in dark mode, where the two are equally close. */}
      {typeof count === "number" && count > 0 && (
        <span
          className={cn(
            "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-mono text-[10px] leading-none",
            active ? "bg-primary/20 text-primary" : "bg-foreground/10 text-foreground",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
